// ===========================================
// Student Layout Context - Provider Component
// ===========================================
// Allows students to toggle between professional and fun dashboard layouts
// Persists preference to localStorage

import { useState, useCallback, useEffect, useContext, ReactNode } from 'react';
import { StudentLayoutContext, StudentLayoutContextValue, LayoutMode } from './StudentLayoutContext.types';
import { AgeGroup } from '../components/brand';

const STORAGE_KEY = 'studentLayoutMode';

interface StudentLayoutProviderProps {
  children: ReactNode;
  defaultAgeGroup?: AgeGroup;
}

// Determine default layout based on age group
function getDefaultLayoutForAge(ageGroup?: AgeGroup): LayoutMode {
  if (!ageGroup) return 'fun';
  if (ageGroup === 'PRESCHOOL' || ageGroup === 'KIDS') return 'fun';
  return 'professional';
}

export function StudentLayoutProvider({ children, defaultAgeGroup }: StudentLayoutProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [layoutMode, setLayoutModeState] = useState<LayoutMode>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'professional' || saved === 'fun') {
        return saved;
      }
    } catch {
      // Ignore errors (SSR, private browsing, etc.)
    }
    // Default based on age group
    return getDefaultLayoutForAge(defaultAgeGroup);
  });

  // Mark as initialized after first render
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Persist to localStorage when mode changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, layoutMode);
    } catch {
      // Ignore errors
    }
  }, [layoutMode]);

  const setLayoutMode = useCallback((mode: LayoutMode) => {
    setLayoutModeState(mode);
  }, []);

  const toggleLayout = useCallback(() => {
    setLayoutModeState((prev) => (prev === 'fun' ? 'professional' : 'fun'));
  }, []);

  const value: StudentLayoutContextValue = {
    layoutMode,
    isInitialized,
    setLayoutMode,
    toggleLayout,
  };

  return (
    <StudentLayoutContext.Provider value={value}>
      {children}
    </StudentLayoutContext.Provider>
  );
}

// Hook to use the student layout context
export function useStudentLayout(): StudentLayoutContextValue {
  const context = useContext(StudentLayoutContext);
  if (!context) {
    throw new Error('useStudentLayout must be used within a StudentLayoutProvider');
  }
  return context;
}
